<template>
  <div class="container-fluid">
    <!-- Bread-crumb-->
    <div class="block-header">
      <bread-crumb></bread-crumb>
    </div>
    <!-- Settings List -->
    <div class="row clearfix">
      <div class="col-lg-12 col-md-12 col-sm-12">
        <div class="card card-top-line">
          <div class="header d-flex align-items-center justify-content-between">
            <h2>Global Settings</h2>
          </div>
          <div class="body pt-0">
            <!-- Table Pagination -->
            <div
              class="py-1 align-items-center justify-content-end table-pagination"
              :class="totalRows > 0 ? ' d-flex' : 'd-none'"
            >
              <div class="number-page">
                <b-pagination
                  class="pagination-small"
                  v-model="currentPage"
                  :total-rows="totalRows"
                  first-text="First"
                  last-text="Last"
                  :per-page="perPage"
                  align="right"
                ></b-pagination>
              </div>
            </div>
            <b-table
              ref="settingsTable"
              responsive
              hover
              outlined
              table-class="js-basic-example table-custom mb-0"
              head-variant="light"
              :items="getSettingsData"
              :fields="fields"
              :current-page="currentPage"
              :per-page="perPage"
              :sort-by.sync="sortBy"
              :sort-desc.sync="sortDesc"
              :sort-direction="sortDirection"
              :emptyText="'No data found.'"
              show-empty
              striped
            >
              <template #head()="{ label, field: { key } }">
                {{ label }}
                <!-- Custom icons -->
                <template>
                  <i v-if="sortBy !== key" class=""></i>
                  <i
                    v-else-if="sortDesc"
                    class="fa fa-long-arrow-down ml-1"
                  ></i>
                  <i v-else class="fa fa-long-arrow-up ml-1"></i>
                </template>
              </template>
              <template #cell(configuration_value)="data">
                <div
                  v-if="isToggleField(data.item.configuration_key)"
                  class="d-flex align-items-center"
                >
                  <!-- Show text when not editing -->
                  <div
                    v-if="!data.item.value.editing"
                    class="d-flex align-items-center"
                  >
                    <span class="mr-2">
                      {{ data.item.value.isChecked ? "Enabled" : "Disabled" }}
                    </span>
                    <template v-if="$store.getters['authpermission/hasAnyPermission'](['settings.edit'])">
                      <i
                        class="mr-1 fa fa-edit text-info cursor-pointer ml-2"
                        @click="data.item.value.editing = true"
                      ></i>
                    </template>
                  </div>
                  <!-- Show toggle when editing -->
                  <div v-else class="custom-switch success-bg">
                    <input
                      type="checkbox"
                      class="custom-switch-input"
                      :id="data.item.id"
                      :checked="data.item.value.isChecked"
                      @click="updateSetting($event, data)"
                    />
                    <label class="custom-switch-label" :for="data.item.id">
                      <span class="custom-switch-text1">Enabled</span>
                      <span class="custom-switch-text2">Disabled</span>
                    </label>
                  </div>
                </div>
                <div v-else class="d-flex align-items-center">
                  <input
                    type="text"
                    class="form-control form-control-sm"
                    v-model="data.item.value.text"
                    :readonly="!data.item.value.editing"
                    style="max-width: 200px"
                    :onkeypress="
                      isNumericField(data.item.configuration_key)
                        ? 'return event.charCode >= 48 && event.charCode <= 57'
                        : null
                    "
                    @input="validateNumericInput(data.item)"
                  />
                  <template v-if="$store.getters['authpermission/hasAnyPermission'](['settings.edit'])">
                    <i
                      class="mr-1 fa fa-edit text-info cursor-pointer ml-2"
                      v-if="!data.item.value.editing"
                      @click="data.item.value.editing = true"
                    ></i>
                    <i
                      class="mr-1 fa fa-save text-success cursor-pointer ml-2"
                      v-if="data.item.value.editing"
                      @click="saveSetting(data)"
                    ></i>
                  </template>
                </div>
              </template>
            </b-table>
            <!-- Table Pagination -->
            <div
              class="py-3 align-items-center justify-content-between table-pagination"
              :class="totalRows > 0 ? ' d-flex' : 'd-none'"
            >
              <div class="per-page">
                <b-form-group
                  label="Per page"
                  label-for="per-page-select"
                  class="mb-0"
                >
                  <b-form-select
                    id="per-page-select"
                    v-model="perPage"
                    :options="pageOptions"
                    size="sm"
                  ></b-form-select>
                </b-form-group>
              </div>
              <div class="number-page">
                <b-pagination
                  v-model="currentPage"
                  :total-rows="totalRows"
                  first-text="First"
                  last-text="Last"
                  :per-page="perPage"
                  align="right"
                ></b-pagination>
              </div>
              <div class="go-page">
                <div class="input-group">
                  <div class="input-group-prepend">
                    <span class="input-group-text">Go To Page</span>
                  </div>
                  <input
                    type="text"
                    v-model="currentPageJump"
                    class="form-control"
                    value=""
                    @keyup.enter="onEnter"
                    onkeypress="return event.charCode >= 48 && event.charCode <= 57"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <main-loader ref="loader"></main-loader>
  </div>
</template>

<script>
import BreadCrumb from "@/components/BreadCrumb";
const LoaderComponent =
  require("@/components/admin/loader/LoaderComponent.vue").default;
import shared from "@/shared.js";

export default {
  name: "settings-component",
  components: {
    BreadCrumb,
    "main-loader": LoaderComponent,
  },
  data() {
    return {
      items: [],
      originalValues: {},

      fields: [
        {
          key: "configuration_title",
          label: "Configuration Title",
          sortable: true,
          thClass: "th_sortfix",
        },
        {
          key: "configuration_value",
          label: "Configuration Value",
          sortable: false,
          thClass: "th_sortfix",
        },
      ],

      totalRows: 0,
      currentPage: 1,
      currentPageJump: 1,
      perPage: 50,
      pageOptions: [50, 100, 150, { value: 100, text: 100 }],
      sortBy: "",
      sortDesc: false,
      sortDirection: "asc",
    };
  },
  methods: {
    onEnter(e) {
      var paginationMenu = Math.ceil(this.totalRows / this.perPage);
      if (e.target.value > 0) {
        if (paginationMenu >= e.target.value) {
          this.currentPage = this.currentPageJump = e.target.value;
        } else {
          this.currentPage = this.currentPageJump = paginationMenu;
        }
      } else {
        this.currentPage = this.currentPageJump = 1;
      }
    },

    updateSetting(event, row) {
      // Show confirmation alert for toggle settings
      if (this.isToggleField(row.item.configuration_key)) {
        const action = event.target.checked ? "enable" : "disable";
        const title = row.item.configuration_title;

        this.$swal
          .fire({
            title: `Confirm ${title} Update`,
            text: `This will ${action} "${title}". Are you sure you want to continue?`,
            icon: "warning",
            customClass: {
              confirmButton: "btn btn-primary btn-lg my-3",
              cancelButton: "btn btn-outline-secondary btn-lg",
            },
            buttonsStyling: false,
            showCancelButton: true,
            confirmButtonText: `Yes, ${action === "enable" ? "Enable" : "Disable"} it`,
            cancelButtonText: "Cancel",
          })
          .then((result) => {
            if (result.isConfirmed) {
              this.$refs.loader.show();
              const newValue = event.target.checked ? "1" : "0";
              this.updateSettingAPI(row.item.id, newValue);
            } else {
              // Revert the toggle state if cancelled
              row.item.value.editing = false;
            }
          });
      } else {
        this.$refs.loader.show();
        const newValue = event.target.checked ? "1" : "0";
        this.updateSettingAPI(row.item.id, newValue);
      }
    },

    saveSetting(row) {
      // Validate numeric fields before saving
      if (this.isNumericField(row.item.configuration_key)) {
        const value = parseFloat(row.item.value.text);
        if (isNaN(value) || value <= 0) {
          shared.toastrError(
            "Please enter a valid positive number greater than 0",
          );
          return;
        }
      }

      this.$refs.loader.show();
      const newValue = row.item.value.text;

      this.updateSettingAPI(row.item.id, newValue);
    },

    // Check if the field should render as a toggle switch
    isToggleField(configurationKey) {
      const toggleFields = ["MM_AUTOPAY", "RBC", "RCN"];
      return toggleFields.includes(configurationKey);
    },

    // Check if the field should accept only numbers
    isNumericField(configurationKey) {
      const numericFields = ["MM_AUTOPAY_MAX_LIMIT"];
      return numericFields.some(
        (field) =>
          configurationKey && configurationKey.toUpperCase().includes(field),
      );
    },

    // Validate numeric input in real-time
    validateNumericInput(item) {
      if (this.isNumericField(item.configuration_key)) {
        // Remove any non-numeric characters except decimal point
        item.value.text = item.value.text.replace(/[^0-9.]/g, "");

        // Ensure only one decimal point
        const parts = item.value.text.split(".");
        if (parts.length > 2) {
          item.value.text = parts[0] + "." + parts.slice(1).join("");
        }

        // Remove leading zeros except for decimal numbers
        if (
          item.value.text.length > 1 &&
          item.value.text[0] === "0" &&
          item.value.text[1] !== "."
        ) {
          item.value.text = item.value.text.substring(1);
        }
      }
    },

    cancelEdit(row) {
      row.item.value.text = this.originalValues[row.item.id];
      row.item.value.editing = false;
    },

    updateSettingAPI(settingId, newValue) {
      const payload = {
        configuration_value: newValue,
      };

      this.$http
        .put(this.$baseurl + "settings/" + settingId, payload, {
          headers: {
            Authorization: "Bearer " + localStorage.getItem("jwtToken"),
          },
        })
        .then((res) => {
          if (res.data.code === "1009-000") {
            this.refreshSettingsTable();
            shared.toastrSuccess(res.data.message);
          } else {
            this.refreshSettingsTable();
            shared.toastrError(res.data.message || "Update failed");
          }
          this.$refs.loader.hide();
        })
        .catch((error) => {
          this.refreshSettingsTable();
          this.$refs.loader.hide();
          const errorMessage =
            error.response?.data?.message ||
            "An error occurred while updating the setting";
          shared.toastrError(errorMessage);
        });
    },

    async getSettingsData(ctx) {
      try {
        this.$refs.loader.show();
        let page = ctx.currentPage || 1;

        this.currentPageJump = page;
        this.items = [];

        const token = localStorage.getItem("jwtToken");
        const response = await this.$http.get(this.$baseurl + "settings/list", {
          headers: {
            Authorization: "Bearer " + token,
          },
        });

        if (response.data.code === "1009-000" && response.data.details) {
          response.data.details.forEach((item) => {
            this.originalValues[item.id] = item.configuration_value;

            let settingData = {
              id: item.id,
              configuration_title: item.configuration_title,
              configuration_key: item.configuration_key,
            };

            if (this.isToggleField(item.configuration_key)) {
              settingData.value = {
                isChecked: item.configuration_value === "1",
                editing: false,
              };
            } else {
              settingData.value = {
                text: item.configuration_value,
                editing: false,
              };
            }

            this.items.push(settingData);
          });
        }

        this.totalRows = this.items.length;
        this.$refs.loader.hide();
        return this.items;
      } catch (error) {
        console.log(error);
        const errorMessage =
          error.response?.data?.message || "Failed to load settings";
        shared.toastrError(errorMessage);
        this.$refs.loader.hide();
        return [];
      }
    },

    refreshSettingsTable() {
      this.$refs.settingsTable.refresh();
    },
  },
  mounted() {},
};
</script>

<style scoped>
.cursor-pointer {
  cursor: pointer;
}

/* Ensure toggle text is visible and properly sized */
.custom-switch .custom-switch-text1,
.custom-switch .custom-switch-text2 {
  font-size: 12px;
  font-weight: 500;
  white-space: nowrap;
  line-height: 1;
}

/* Adjust toggle width to accommodate longer text */
.custom-switch .custom-switch-label {
  min-width: 85px;
  padding-left: 12px;
  padding-right: 12px;
}
</style>